import * as React from "react";
import { Drawer } from "vaul";
import { cn } from "@repo/share-log-web-utils";

const THEME_COLOR_META_ID = "theme-color-meta";
const THEME_COLORS = {
  light: "#ffffff",
  dark: "#030712",
} as const;
// Same visual result as bg-black/40 composited over the light/dark theme color.
const DIMMED_THEME_COLORS = {
  light: "#999999",
  dark: "#02040b",
} as const;
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;

let dimmedThemeColorLocks = 0;
let dimmedThemeColorObserver: MutationObserver | null = null;

function getCurrentTheme() {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function getThemeColorMeta() {
  return document.getElementById(THEME_COLOR_META_ID) as HTMLMetaElement | null;
}

function applyDimmedThemeColor() {
  const meta = getThemeColorMeta();
  if (!meta) return;
  meta.content = DIMMED_THEME_COLORS[getCurrentTheme()];
}

function restoreThemeColor() {
  const meta = getThemeColorMeta();
  if (!meta) return;
  meta.content = THEME_COLORS[getCurrentTheme()];
}

function acquireDimmedThemeColor() {
  if (typeof document === "undefined") {
    return () => {};
  }

  dimmedThemeColorLocks += 1;
  applyDimmedThemeColor();

  if (
    !dimmedThemeColorObserver &&
    typeof MutationObserver !== "undefined"
  ) {
    dimmedThemeColorObserver = new MutationObserver(applyDimmedThemeColor);
    dimmedThemeColorObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    dimmedThemeColorLocks = Math.max(0, dimmedThemeColorLocks - 1);

    if (dimmedThemeColorLocks === 0) {
      dimmedThemeColorObserver?.disconnect();
      dimmedThemeColorObserver = null;
      restoreThemeColor();
    }
  };
}

function useDimmedThemeColor(isOpen: boolean) {
  useIsomorphicLayoutEffect(() => {
    if (!isOpen) return;
    return acquireDimmedThemeColor();
  }, [isOpen]);
}

function BottomSheet({
  children,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof Drawer.Root>) {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const isOpen = isControlled ? open : uncontrolledOpen;

  useDimmedThemeColor(isOpen);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return (
    <Drawer.Root
      noBodyStyles
      repositionInputs={false}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      {...props}
    >
      {children}
    </Drawer.Root>
  );
}

const BottomSheetTrigger: typeof Drawer.Trigger = Drawer.Trigger;

const BottomSheetClose: typeof Drawer.Close = Drawer.Close;

const BottomSheetPortal: typeof Drawer.Portal = Drawer.Portal;

function BottomSheetOverlay({
  className,
  style,
  ...props
}: React.ComponentProps<typeof Drawer.Overlay>) {
  return (
    <Drawer.Overlay
      className={cn("fixed inset-x-0 bottom-0 z-50 bg-black/40", className)}
      style={{
        top: "calc(var(--safe-area-inset-top) * -1)",
        ...style,
      }}
      {...props}
    />
  );
}

function BottomSheetContent({
  className,
  style,
  children,
  ...props
}: React.ComponentProps<typeof Drawer.Content>) {
  return (
    <BottomSheetPortal>
      <BottomSheetOverlay />
      <Drawer.Content
        // 내부 입력창에 포커스되면 키보드 바로 위로 스크롤한다 (lib/keyboard-focus.ts)
        data-keyboard-aware
        className={cn(
          "fixed inset-x-0 z-50 flex flex-col rounded-t-2xl border-t bg-background",
          className,
        )}
        style={{
          // 키보드가 열리면 그만큼 위로 띄운다. iOS visual viewport 보정 포함.
          bottom: "var(--keyboard-inset)",
          // 키보드가 열리면 보이는 영역이 줄어드므로 시트 높이도 함께 줄인다.
          maxHeight: "min(90dvh, var(--bottom-sheet-available-height))",
          // 키보드 위에 떠 있는 동안에는 홈 인디케이터 여백이 필요 없다.
          paddingBottom:
            "calc(var(--safe-area-inset-bottom) * (1 - var(--keyboard-open)))",
          ...style,
        }}
        {...props}
      >
        <div className="mx-auto mt-3 mb-2 h-1.5 w-12 shrink-0 rounded-full bg-muted-foreground/20" />
        {children}
      </Drawer.Content>
    </BottomSheetPortal>
  );
}

function BottomSheetHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-col gap-1.5 px-4 pb-3 text-center", className)}
      {...props}
    />
  );
}

function BottomSheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof Drawer.Title>) {
  return (
    <Drawer.Title
      className={cn("text-lg font-semibold text-foreground", className)}
      {...props}
    />
  );
}

function BottomSheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof Drawer.Description>) {
  return (
    <Drawer.Description
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

function BottomSheetFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-col gap-2 px-4 py-3", className)}
      {...props}
    />
  );
}

export {
  BottomSheet,
  BottomSheetTrigger,
  BottomSheetClose,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetDescription,
  BottomSheetFooter,
};
